/**
 * 워크플로 마법사 — 선택한 유형·데이터·옵션으로 캔버스(노드/엣지)를 만든다.
 * UI 와 분리된 순수 함수라 미리보기·생성이 같은 결과를 쓴다.
 *
 * DFD 규칙(데이터 ↔ 처리 교대, 시작·끝은 데이터)을 지켜서 만든다 — 만들어진 캔버스는 DFD 검사에 바로 '적합'.
 *
 * 노드 이름·요약·검증 문구는 **만드는 순간의 언어**로 캔버스에 저장된다(데이터). 뒤에 언어를 바꿔도 그대로다.
 */
import type { DataDefinition } from '../../../store/dataDefinitionsSlice';
import i18n from '../../../i18n';
import { typeLabelOf } from '../nodeCatalog';
import { pruneDecorativeData } from '../utils/dfdRules';

const tr = (key: string, opts?: Record<string, unknown>) => String(i18n.t(`wizard:${key}`, opts as any));
const defKind = (d: DataDefinition) => typeLabelOf(d.type === 'sam' ? 'csvData' : 'rdbData');

export type FlowKind = 'db2db' | 'file2db' | 'db2file' | 'file2file';
export type OptionKind = 'basic' | 'verify' | 'partition';
export type PartitionMode = 'range' | 'dblink';
/** 소스/타깃 지정 방식 — 데이터 정의(기존/신규) 또는 SQL 로 대체 */
export type EndpointMode = 'definition' | 'sql';

export interface EndpointSpec {
  mode: EndpointMode;
  /** definition: 선택했거나 새로 만든 데이터 정의 */
  definition?: DataDefinition | null;
  /** sql: 직접 작성한 SQL (소스=조회, 타깃=적재/후처리) */
  sql?: string;
  /** sql 모드에서 쓸 커넥션 */
  connection?: string;
}

export interface WizardSpec {
  kind: FlowKind;
  name: string;
  description?: string;
  source: EndpointSpec;
  target: EndpointSpec;
  option: OptionKind;
  /** 대용량 분할 */
  partition: {
    mode: PartitionMode;
    count: number;      // 분할 수 (기본 8)
    keyColumn: string;  // 분할 기준 컬럼 (숫자/해시 대상)
    dbLinkName?: string; // dblink 방식에서 타깃으로 넘길 DB link 이름
    tempPrefix?: string; // 임시 분할 테이블 접두어
  };
  /** 적재 방식 */
  load: { mode: 'WRITE' | 'APPEND' | 'UPSERT'; keyColumns: string[]; useBulkLoader: boolean };
}

export interface BuiltCanvas { nodes: any[]; edges: any[]; summary: string[] }

const GAP_X = 260;
const GAP_Y = 130;
const X = (i: number) => 40 + i * GAP_X;

let seq = 0;
const nid = (p: string) => `${p}_${Date.now().toString(36)}_${(seq++).toString(36)}`;

function node(id: string, type: string, label: string, x: number, y: number, config: Record<string, any> = {}) {
  return {
    id, type, position: { x, y },
    data: { label, config: { label, ...config } },
    draggable: true, className: 'workflow-child-node', style: { zIndex: 10 },
  };
}
const edge = (s: string, t: string, sourceHandle?: string) => ({
  id: `${s}-${t}`, source: s, target: t, type: 'workflow', ...(sourceHandle ? { sourceHandle } : {}),
});

/** 데이터 정의 → 데이터 노드 config (데이터 목록 드래그와 같은 매핑) */
function dataNodeFromDef(def: DataDefinition, mode: 'READ' | 'WRITE') {
  if (def.type === 'sam') {
    return {
      type: 'csvData',
      config: {
        filepath: def.source.filepath || '', delimiter: def.source.delimiter || ',',
        hasHeader: def.source.hasHeader ?? true, encoding: (def.source as any).encoding || 'UTF-8', mode,
        format: (def.source as any).format || 'delimited',
        fieldWidths: (def.source as any).fieldWidths || '', fieldNames: (def.source as any).fieldNames || '',
        tableColumns: def.columns.map((c) => ({ name: c.name, dataType: c.dataType, comment: '' })),
        dataDefinitionId: def.id,
      },
    };
  }
  return {
    type: 'rdbData',
    config: {
      connection: def.source.connection || '', schema: def.source.schema || '', table: def.source.table || '', mode, writeMode: 'INSERT',
      columns: def.columns.map((c) => c.name),
      tableColumns: def.columns.map((c) => ({ name: c.name, dataType: c.dataType, comment: '' })),
      dataDefinitionId: def.id,
    },
  };
}

const defName = (e: EndpointSpec, fallback: string) => e.definition?.name || fallback;
/** SQL 에 쓸 테이블 이름 — 커넥션은 접속 계정이라 스키마까지 붙여야 다른 스키마의 동명 테이블과 섞이지 않는다 */
const defTable = (e: EndpointSpec) =>
  (e.definition?.type === 'db' ? qualified(e.definition.source.schema, e.definition.source.table) : '');
const qualified = (schema?: string, table?: string) =>
  (schema || '').trim() && (table || '').trim() ? `${schema!.trim()}.${table!.trim()}` : (table || '');
const defConn = (e: EndpointSpec) => e.connection || (e.definition?.type === 'db' ? e.definition.source.connection || '' : '');

/** 검증 오류 — step 은 그 오류를 보여 줄 마법사 단계 (문구로 단계를 가르지 않는다) */
export interface SpecError { step: 'source' | 'target' | 'option' | 'confirm'; message: string }

/** 마법사 입력 검증 — 사용자가 다음 단계로 갈 수 있는지 */
export function validateSpec(spec: WizardSpec): SpecError[] {
  const errs: SpecError[] = [];
  const add = (step: SpecError['step'], key: string) => errs.push({ step, message: tr(key) });
  if (!spec.name.trim()) add('confirm', 'err.name');
  const needSrcDef = spec.source.mode === 'definition';
  const needTgtDef = spec.target.mode === 'definition';
  if (needSrcDef && !spec.source.definition) add('source', 'err.srcDef');
  if (!needSrcDef && !(spec.source.sql || '').trim()) add('source', 'err.srcSql');
  if (!needSrcDef && !defConn(spec.source)) add('source', 'err.srcConn');
  if (needTgtDef && !spec.target.definition) add('target', 'err.tgtDef');
  if (!needTgtDef && !(spec.target.sql || '').trim()) add('target', 'err.tgtSql');
  if (!needTgtDef && !defConn(spec.target)) add('target', 'err.tgtConn');
  if (spec.load.mode === 'UPSERT' && spec.load.keyColumns.length === 0) add('target', 'err.upsertKey');
  if (spec.option === 'partition') {
    if (spec.kind !== 'db2db') add('option', 'err.partitionKind');
    if (!spec.partition.keyColumn.trim()) add('option', 'err.partitionKey');
    if (spec.partition.count < 2 || spec.partition.count > 32) add('option', 'err.partitionCount');
    if (spec.partition.mode === 'dblink' && !(spec.partition.dbLinkName || '').trim()) add('option', 'err.dbLink');
    if (spec.partition.mode === 'dblink' && (spec.source.mode === 'sql' || spec.target.mode === 'sql')) add('option', 'err.dbLinkDefs');
  }
  return errs;
}

/** 소스가 SQL 모드일 때 SQL실행 노드로 시작하고, 데이터 정의면 데이터 노드 + 데이터처리로 시작한다 */
function buildHead(spec: WizardSpec, out: BuiltCanvas, col: number): { lastId: string; nextCol: number } {
  const y = 120;
  if (spec.source.mode === 'sql') {
    const sq = nid('sql_src');
    out.nodes.push(node(sq, 'sqlScript', tr('node.sourceQuery'), X(col), y, {
      connection: defConn(spec.source), sql: spec.source.sql || '', params: [], fetchResult: true,
    }));
    const d = nid('data_src');
    out.nodes.push(node(d, 'innerData', tr('node.sourceData'), X(col + 1), y));
    out.edges.push(edge(sq, d));
    out.summary.push(tr('summary.srcSql', { conn: defConn(spec.source) }));
    return { lastId: d, nextCol: col + 2 };
  }
  const def = spec.source.definition!;
  const dn = dataNodeFromDef(def, 'READ');
  const src = nid('src');
  out.nodes.push(node(src, dn.type, defName(spec.source, tr('node.source')), X(col), y, dn.config));
  const ext = nid('extract');
  out.nodes.push(node(ext, 'transformLoad', tr('node.extract'), X(col + 1), y, { transformMode: 'MOVE', mapping: [], mode: 'WRITE', useBulkLoader: false }));
  const d = nid('data_src');
  out.nodes.push(node(d, 'innerData', tr('node.sourceData'), X(col + 2), y));
  out.edges.push(edge(src, ext), edge(ext, d));
  out.summary.push(tr('summary.src', { kind: defKind(def), name: def.name }));
  return { lastId: d, nextCol: col + 3 };
}

/** 마지막 내부데이터 → 타깃(데이터 정의 적재 또는 SQL 실행) */
function buildTail(spec: WizardSpec, out: BuiltCanvas, fromId: string, col: number): { lastId: string; nextCol: number } {
  const y = 120;
  if (spec.target.mode === 'sql') {
    const sq = nid('sql_tgt');
    out.nodes.push(node(sq, 'sqlScript', tr('node.targetSql'), X(col), y, {
      connection: defConn(spec.target), sql: spec.target.sql || '', params: [], fetchResult: false,
    }));
    const d = nid('data_tgt');
    out.nodes.push(node(d, 'innerData', tr('node.result'), X(col + 1), y));
    out.edges.push(edge(fromId, sq), edge(sq, d));
    out.summary.push(tr('summary.tgtSql', { conn: defConn(spec.target) }));
    return { lastId: d, nextCol: col + 2 };
  }
  const def = spec.target.definition!;
  const dn = dataNodeFromDef(def, 'WRITE');
  const ld = nid('load');
  out.nodes.push(node(ld, 'transformLoad', tr('node.load'), X(col), y, {
    transformMode: 'MOVE', mapping: [], mode: spec.load.mode, useBulkLoader: spec.load.useBulkLoader,
    ...(spec.load.mode === 'UPSERT' ? { keyColumns: spec.load.keyColumns } : {}),
  }));
  const tgt = nid('tgt');
  out.nodes.push(node(tgt, dn.type, defName(spec.target, tr('node.target')), X(col + 1), y, dn.config));
  out.edges.push(edge(fromId, ld), edge(ld, tgt));
  out.summary.push(tr('summary.tgt', { kind: defKind(def), name: def.name, mode: spec.load.mode, bulk: spec.load.useBulkLoader ? tr('summary.bulk') : '' }));
  return { lastId: tgt, nextCol: col + 2 };
}

/** 신뢰 옵션 — 적재 뒤 건수 검증 */
function appendVerify(spec: WizardSpec, out: BuiltCanvas, fromId: string, col: number, shell = true): void {
  const y = 120;
  const srcIsDb = spec.source.mode === 'sql' || spec.source.definition?.type === 'db';
  const tgtIsDb = spec.target.mode === 'sql' || spec.target.definition?.type === 'db';
  const conn = defConn(spec.target) || defConn(spec.source);

  if (srcIsDb && tgtIsDb && spec.source.mode === 'definition' && spec.target.mode === 'definition') {
    // 소스·타깃이 모두 DB 테이블이면 한 번의 조회로 양쪽 건수를 비교한다
    const sq = nid('sql_verify');
    const srcT = defTable(spec.source), tgtT = defTable(spec.target);
    out.nodes.push(node(sq, 'sqlScript', tr('node.countQuery'), X(col), y, {
      connection: conn, params: [], fetchResult: true,
      sql: `SELECT (SELECT COUNT(*) FROM ${srcT}) AS src_cnt,\n       (SELECT COUNT(*) FROM ${tgtT}) AS tgt_cnt;`,
    }));
    const d = nid('data_cnt');
    out.nodes.push(node(d, 'innerData', tr('node.count'), X(col + 1), y));
    const qc = nid('quality');
    out.nodes.push(node(qc, 'quality', tr('node.countCheck'), X(col + 2), y, {
      onViolation: 'fail', maxViolationPct: '',
      rules: [{ column: 'tgt_cnt', rule: 'expr', param: 'src_cnt == tgt_cnt', message: tr('node.countMismatch') }],
    }));
    const d2 = nid('data_ok');
    out.nodes.push(node(d2, 'innerData', tr('node.checkResult'), X(col + 3), y));
    out.edges.push(edge(fromId, sq), edge(sq, d), edge(d, qc), edge(qc, d2));
    out.summary.push(tr('summary.verifyBoth'));
    return;
  }

  // 한쪽이 파일이면 타깃 건수만 확인한다 (0건 방지)
  if (tgtIsDb) {
    const tgtT = defTable(spec.target);
    const sq = nid('sql_verify');
    out.nodes.push(node(sq, 'sqlScript', tr('node.targetCount'), X(col), y, {
      connection: conn, params: [], fetchResult: true,
      sql: `SELECT COUNT(*) AS tgt_cnt FROM ${tgtT};`,
    }));
    const d = nid('data_cnt');
    out.nodes.push(node(d, 'innerData', tr('node.count'), X(col + 1), y));
    const qc = nid('quality');
    out.nodes.push(node(qc, 'quality', tr('node.countCheck'), X(col + 2), y, {
      onViolation: 'fail', maxViolationPct: '',
      rules: [{ column: 'tgt_cnt', rule: 'expr', param: 'tgt_cnt > 0', message: tr('node.targetEmpty') }],
    }));
    const d2 = nid('data_ok');
    out.nodes.push(node(d2, 'innerData', tr('node.checkResult'), X(col + 3), y));
    out.edges.push(edge(fromId, sq), edge(sq, d), edge(d, qc), edge(qc, d2));
    out.summary.push(tr('summary.verifyTarget'));
    return;
  }

  // 타깃이 파일이면 셸로 행 수를 센다 (헤더 제외) — 외부명령어를 쓸 수 없는 사람·서버면 넣지 않고 알린다
  if (!shell) { out.summary.push(tr('summary.verifyFileNoShell')); return; }
  const path = spec.target.definition?.source.filepath || '';
  const sh = nid('shell_verify');
  out.nodes.push(node(sh, 'shellCommand', tr('node.fileRows'), X(col), y, {
    command: `f="/opt/airflow/dags/samples/${path}"\nn=$(( $(wc -l < "$f") - 1 ))\necho "rows=$n"\ntest "$n" -gt 0`,
    params: [], cwd: '', timeoutSecs: 600,
  }));
  const d = nid('data_ok');
  out.nodes.push(node(d, 'innerData', tr('node.checkResult'), X(col + 1), y));
  out.edges.push(edge(fromId, sh), edge(sh, d));
  out.summary.push(tr('summary.verifyFile'));
}

/** 대용량 분할 A — 소스를 범위로 나눠 병렬로 읽고 합류해서 한 번에 적재 */
function buildRangePartition(spec: WizardSpec, out: BuiltCanvas): void {
  const n = spec.partition.count;
  const key = spec.partition.keyColumn;
  const srcDef = spec.source.definition!;
  const srcTable = qualified(srcDef.source.schema, srcDef.source.table);
  const cols = srcDef.columns.map((c) => c.name).join(', ') || '*';
  const midY = 120 + ((n - 1) * GAP_Y) / 2;

  const src = nid('src');
  const dn = dataNodeFromDef(srcDef, 'READ');
  out.nodes.push(node(src, dn.type, defName(spec.source, tr('node.source')), X(0), midY, dn.config));
  const br = nid('branch');
  out.nodes.push(node(br, 'branch', tr('node.split', { n }), X(1), midY, { condition: '', defaultPath: 'TRUE' }));
  out.edges.push(edge(src, br));

  const mg = nid('merge');
  for (let k = 0; k < n; k++) {
    const y = 120 + k * GAP_Y;
    const p = nid(`part${k}`);
    out.nodes.push(node(p, 'transformLoad', tr('node.partExtract', { k }), X(2), y, {
      transformMode: 'MOVE', mapping: [], mode: 'WRITE', useBulkLoader: false,
      extractMode: 'QUERY',
      query: `SELECT ${cols} FROM ${srcTable} WHERE MOD(${key}, ${n}) = ${k}`,
    }));
    const d = nid(`pdata${k}`);
    out.nodes.push(node(d, 'innerData', tr('node.partData', { k }), X(3), y));
    out.edges.push(edge(br, p), edge(p, d), edge(d, mg));
  }
  // 합류(제어)는 DFD 에서 투명한 통과점이라 뒤에 내부데이터를 두면 '데이터 → 데이터' 가 된다 → 바로 적재로 잇는다
  out.nodes.push(node(mg, 'merge', tr('node.merge'), X(4), midY, { joinType: 'ALL' }));
  const tail = buildTail(spec, out, mg, 5);
  out.summary.push(tr('summary.range', { n, key }));
  if (spec.option === 'partition' && spec.load.mode !== 'APPEND') {
    out.summary.push(tr('summary.rangeSafe'));
  }
  // 적재·타깃은 분기 묶음의 세로 가운데에 둔다
  [tail.lastId].forEach((id) => { const nd = out.nodes.find((x) => x.id === id); if (nd) nd.position.y = midY; });
  out.nodes.filter((nd) => nd.type === 'transformLoad' && nd.position.y === 120).forEach((nd) => { nd.position.y = midY; });
}

/** 대용량 분할 B — 소스/타깃 임시 분할 테이블 + DB link 병렬 적재 후 머지 */
function buildDbLinkPartition(spec: WizardSpec, out: BuiltCanvas): void {
  const n = spec.partition.count;
  const key = spec.partition.keyColumn;
  const link = spec.partition.dbLinkName || 'TGT_LINK';
  const prefix = spec.partition.tempPrefix || 'of_tmp';
  const srcDef = spec.source.definition!;
  const tgtDef = spec.target.definition!;
  const srcTable = qualified(srcDef.source.schema, srcDef.source.table);
  const tgtTable = qualified(tgtDef.source.schema, tgtDef.source.table);
  const srcConn = srcDef.source.connection || '';
  const cols = srcDef.columns.map((c) => c.name).join(', ') || '*';
  const midY = 120 + ((n - 1) * GAP_Y) / 2;

  const src = nid('src');
  const dn = dataNodeFromDef(srcDef, 'READ');
  out.nodes.push(node(src, dn.type, defName(spec.source, tr('node.source')), X(0), midY, dn.config));

  // 1) 임시 분할 테이블 생성 (소스 쪽 + DB link 로 타깃 쪽)
  const prep = nid('sql_prep');
  const prepSql = [
    ...Array.from({ length: n }, (_, k) =>
      `DROP TABLE IF EXISTS ${prefix}_src_${k};\nCREATE TABLE ${prefix}_src_${k} AS SELECT ${cols} FROM ${srcTable} WHERE MOD(${key}, ${n}) = ${k};`),
    ...Array.from({ length: n }, (_, k) =>
      `DROP TABLE IF EXISTS ${prefix}_tgt_${k}@${link};\nCREATE TABLE ${prefix}_tgt_${k}@${link} AS SELECT ${cols} FROM ${srcTable} WHERE 1 = 0;`),
  ].join('\n');
  out.nodes.push(node(prep, 'sqlScript', tr('node.createTemp'), X(1), midY, {
    connection: srcConn, params: [], fetchResult: false, sql: prepSql,
  }));
  const pd = nid('data_prep');
  out.nodes.push(node(pd, 'innerData', tr('node.ready'), X(2), midY));
  out.edges.push(edge(src, prep), edge(prep, pd));

  // 2) 팬아웃 → 파티션별 DB link 적재
  const br = nid('branch');
  out.nodes.push(node(br, 'branch', tr('node.split', { n }), X(3), midY, { condition: '', defaultPath: 'TRUE' }));
  out.edges.push(edge(pd, br));
  const mg = nid('merge');
  for (let k = 0; k < n; k++) {
    const y = 120 + k * GAP_Y;
    const p = nid(`plink${k}`);
    out.nodes.push(node(p, 'sqlScript', tr('node.partSend', { k }), X(4), y, {
      connection: srcConn, params: [], fetchResult: false,
      sql: `INSERT INTO ${prefix}_tgt_${k}@${link} (${cols})\nSELECT ${cols} FROM ${prefix}_src_${k};`,
    }));
    const d = nid(`pldata${k}`);
    out.nodes.push(node(d, 'innerData', tr('node.partSent', { k }), X(5), y));
    out.edges.push(edge(br, p), edge(p, d), edge(d, mg));
  }
  out.nodes.push(node(mg, 'merge', tr('node.sendMerge'), X(6), midY, { joinType: 'ALL' }));

  // 3) 타깃에서 임시 테이블 머지 → 최종 타깃, 임시 정리 (합류 → SQL실행 직접 연결)
  const merge = nid('sql_merge');
  const mergeSql = [
    spec.load.mode === 'WRITE' ? `TRUNCATE TABLE ${tgtTable};` : null,
    ...Array.from({ length: n }, (_, k) => `INSERT INTO ${tgtTable} (${cols}) SELECT ${cols} FROM ${prefix}_tgt_${k};`),
    ...Array.from({ length: n }, (_, k) => `DROP TABLE IF EXISTS ${prefix}_tgt_${k};`),
  ].filter(Boolean).join('\n');
  out.nodes.push(node(merge, 'sqlScript', tr('node.targetMerge'), X(7), midY, {
    connection: tgtDef.source.connection || '', params: [], fetchResult: false, sql: mergeSql,
  }));
  const tgt = nid('tgt');
  const tdn = dataNodeFromDef(tgtDef, 'WRITE');
  out.nodes.push(node(tgt, tdn.type, defName(spec.target, tr('node.target')), X(8), midY, tdn.config));
  out.edges.push(edge(mg, merge), edge(merge, tgt));

  // 소스 임시 테이블 정리
  const cleanup = nid('sql_cleanup');
  out.nodes.push(node(cleanup, 'sqlScript', tr('node.sourceCleanup'), X(9), midY, {
    connection: srcConn, params: [], fetchResult: false,
    sql: Array.from({ length: n }, (_, k) => `DROP TABLE IF EXISTS ${prefix}_src_${k};`).join('\n'),
  }));
  const done = nid('data_done');
  out.nodes.push(node(done, 'innerData', tr('node.done'), X(10), midY));
  out.edges.push(edge(tgt, cleanup), edge(cleanup, done));

  out.summary.push(tr('summary.dblink', { n, link }));
  out.summary.push(tr('summary.dblinkNote'));
}

/** 마법사 입력 → 캔버스 */
export function buildWorkflow(spec: WizardSpec, opts: { shell?: boolean } = {}): BuiltCanvas {
  const out: BuiltCanvas = { nodes: [], edges: [], summary: [] };
  if (spec.option === 'partition' && spec.kind === 'db2db') {
    if (spec.partition.mode === 'dblink') buildDbLinkPartition(spec, out);
    else buildRangePartition(spec, out);
    return prune(out);
  }
  const head = buildHead(spec, out, 0);
  const tail = buildTail(spec, out, head.lastId, head.nextCol);
  if (spec.option === 'verify') appendVerify(spec, out, tail.lastId, tail.nextCol, opts.shell !== false);
  else out.summary.push(tr('summary.basic'));
  return prune(out);
}

/**
 * 만들어 둔 그림에서 **이름표만 남은 내부데이터**를 걷어낸다.
 *
 * 위의 조립 함수들은 단계마다 내부데이터를 하나씩 두는데, 앞뒤가 모두 실행·제어 노드면 그 사이로
 * 흐르는 데이터가 없어 뜻 없는 노드가 된다. 조립 쪽을 하나하나 고치는 대신 마지막에 한 번 걷는다 —
 * 어떤 조합으로 붙든 같은 규칙이 적용된다.
 */
function prune(out: BuiltCanvas): BuiltCanvas {
  const { nodes, edges } = pruneDecorativeData(out.nodes as any, out.edges as any);
  return { ...out, nodes: nodes as any, edges: edges as any };
}

export const FLOW_KINDS: FlowKind[] = ['db2db', 'file2db', 'db2file', 'file2file'];
/** 유형 이름·설명 — 렌더할 때 부른다(모듈 상수로 두면 언어를 바꿔도 갱신되지 않는다) */
export const flowKindLabel = (k: FlowKind) => tr(`kind.${k}`);
export const flowKindDesc = (k: FlowKind) => tr(`kindDesc.${k}`);

/** 유형별로 소스/타깃이 어떤 데이터 정의여야 하는지 */
export function endpointTypes(kind: FlowKind): { source: 'db' | 'sam'; target: 'db' | 'sam' } {
  switch (kind) {
    case 'db2db': return { source: 'db', target: 'db' };
    case 'file2db': return { source: 'sam', target: 'db' };
    case 'db2file': return { source: 'db', target: 'sam' };
    default: return { source: 'sam', target: 'sam' };
  }
}

/**
 * 미리보기용 — 아직 고르지 않은 소스/타깃 자리에 '(미지정)' 임시 정의를 끼워 넣는다.
 * 유형만 골라도 기본 구성이 그려지고, 옵션을 바꾸면 그림이 따라 바뀐다.
 */
export function previewSpec(spec: WizardSpec): WizardSpec {
  const kinds = endpointTypes(spec.kind);
  const placeholder = (t: 'db' | 'sam', label: string): DataDefinition => ({
    id: `__preview_${t}_${label}`, name: label, type: t, description: '',
    source: t === 'db' ? { connection: '', table: '' } : { filepath: '', delimiter: ',', hasHeader: true },
    columns: [],
  } as unknown as DataDefinition);

  const fill = (e: EndpointSpec, t: 'db' | 'sam', label: string): EndpointSpec =>
    e.mode === 'sql' ? { ...e, sql: e.sql || tr('preview.noSql') } : (e.definition ? e : { ...e, definition: placeholder(t, label) });

  const s: WizardSpec = {
    ...spec,
    source: fill(spec.source, kinds.source, tr('preview.unsetSource', { kind: typeLabelOf(kinds.source === 'db' ? 'rdbData' : 'csvData') })),
    target: fill(spec.target, kinds.target, tr('preview.unsetTarget', { kind: typeLabelOf(kinds.target === 'db' ? 'rdbData' : 'csvData') })),
  };
  // 분할은 기준 컬럼이 없어도 그림은 그려 준다
  if (s.option === 'partition' && !s.partition.keyColumn) s.partition = { ...s.partition, keyColumn: tr('preview.keyColumn') };
  return s;
}

/** 미리보기 캔버스 — 입력이 덜 채워져 있어도 실패하지 않는다 */
export function buildPreview(spec: WizardSpec, opts: { shell?: boolean } = {}): BuiltCanvas {
  try {
    return buildWorkflow(previewSpec(spec), opts);
  } catch {
    return { nodes: [], edges: [], summary: [] };
  }
}

export function defaultSpec(kind: FlowKind = 'db2db'): WizardSpec {
  return {
    kind, name: '', description: '',
    source: { mode: 'definition', definition: null, sql: '', connection: '' },
    target: { mode: 'definition', definition: null, sql: '', connection: '' },
    option: 'basic',
    partition: { mode: 'range', count: 8, keyColumn: '', dbLinkName: '', tempPrefix: 'of_tmp' },
    load: { mode: 'WRITE', keyColumns: [], useBulkLoader: false },
  };
}
